<template>
  <div>
    <v-container class="container">
      <v-row v-if="ajaxLoaded">
        <v-col cols="12" lg="8" md="8" v-if="hasCourses" class="pr-md-4">
          <courses-panel v-model="activeTab" :tabs="courseTabs" :show-add-button="false" rounded>
            <template v-if="activeTab === 'past' && deadlineApproachingCodes.length" #banner>
              <transcript-deadline-banner :course-codes="deadlineApproachingCodes" />
            </template>

            <template #subheader="{ activeTab: tab }">{{ subheaderText(tab) }}</template>

            <template #default="{ activeTab: tab }">
              <template v-if="groupsFor(tab).length">
                <template v-for="group in groupsFor(tab)" :key="group.key">
                  <courses-section-heading v-if="group.section" v-bind="sectionCopy(group.section)" />
                  <my-courses
                    v-for="(course, i) in group.courses"
                    :key="`${group.key}-${course._enrollmentId ?? i}`"
                    v-bind="courseProps(course, group.title)"
                    @openCloseUnenrollModel="openCloseUnenrollModel"
                    @reloadDashboardOrPathway="reloadCourses"
                  />
                </template>
              </template>
              <courses-empty-state v-else v-bind="emptyState(tab)" @cta="handleAddCoursesClick" />
            </template>
          </courses-panel>
        </v-col>
        <v-col cols="12" lg="8" md="8" v-else class="pr-md-4">
          <div style="border: 1px solid #e0e0e0; border-radius: 12px; padding: 20px; margin-bottom: 24px">
            <div class="d-flex align-center pb-3">
              <div class="font-weight-bold" :style="{ fontSize: $vuetify.display.xs ? '16px' : '20px' }">
                My Courses
              </div>
            </div>
            <p class="empty-state-text">You do not have any courses here.</p>
            <UiButton variant="dark-outline" size="medium" @click="handleAddCoursesClick"> Add Courses </UiButton>
          </div>
        </v-col>
        <v-col cols="12" lg="4" md="4">
          <v-row>
            <v-col cols="12">
              <congrats-yellow v-show="hasTranscriptedCourses && false" />
              <credit-progress
                :earnedCredits="earnedCredits"
                :totalCredits="totalCredits"
                v-if="!shouldRemoveCreditTracker()"
              />
              <current-GPA :GPA="currentGPA">
                <template #header>
                  <div class="d-flex align-center">
                    <v-icon color="#ffc527" class="mr-2 mt-2 ml-0">mdi-chevron-right</v-icon>
                    <span style="font-size: 16px">Enter your anticipated grades.</span>
                  </div>
                </template>
              </current-GPA>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <unenroll-dialog
      :show="unenrollCourse"
      :courseInfo="Object.assign({}, courseUnenrollInfo.courseInfo)"
      :paymentMethod="courseUnenrollInfo.paymentMethod"
      :enrollmentModeVerifiedDate="courseUnenrollInfo.enrollmentModeVerifiedDate"
      :courseDropDeadline="courseUnenrollInfo.courseDropDeadline"
      :enrollmentId="courseUnenrollInfo.enrollmentId"
      :buttonAction="courseUnenrollInfo.buttonAction"
      :enrollmentMode="courseUnenrollInfo.enrollmentMode"
      :isCourseRefunded="courseUnenrollInfo.isCourseRefunded"
      :verifiedTransactionId="courseUnenrollInfo.verifiedTransactionId"
      @openCloseUnenrollModel="openCloseUnenrollModel"
      @doUnenroll="doUnenroll"
    />
  </div>
</template>
<style scoped>
.empty-state-text {
  font-weight: 500;
  margin: 0 0 16px 0;
  color: #747474;
}
</style>
<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';
import { Button as UiButton } from '@/components/ui';
import UnenrollDialog from '@/components/UnenrollDialog.vue';
import CoursesPanel from '@/components/courses/CoursesPanel.vue';
import CoursesEmptyState from '@/components/courses/CoursesEmptyState.vue';
import CoursesSectionHeading from '@/components/courses/CoursesSectionHeading.vue';
import TranscriptDeadlineBanner from '@/components/courses/TranscriptDeadlineBanner.vue';
import { emptyStateFor } from '@/components/courses/courseEmptyStates';
import { sectionCopyFor } from '@/components/courses/courseSectionCopy';
import { courseGroupsFor } from '@/components/courses/courseGroups';
import { subheaderTextFor } from '@/components/courses/courseSubheader';
import { deadlineApproachingCourseCodes } from '@/components/courses/courseTranscriptState';

import { SessionInitializer } from '../mixins/sessionInitializer';
import { Helper } from '../mixins/helpers.js';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';

export default defineComponent({
  // NOT 'MyCourses' — the course card is globally registered as <my-courses>;
  // matching that name here makes the card loops self-resolve into infinite recursion.
  name: 'MyCoursesPage',
  components: {
    UiButton,
    UnenrollDialog,
    CoursesPanel,
    CoursesEmptyState,
    CoursesSectionHeading,
    TranscriptDeadlineBanner
  },
  mixins: [SessionInitializer, Helper, DataLayerMixin],

  data() {
    return {
      earnedCredits: 0,
      totalCredits: 0,
      currentGPA: '0.0',
      activeTab: 'all',
      ajaxLoaded: false,
      unenrollCourse: false,
      courseUnenrollInfo: {}
    };
  },

  props: {
    refreshDashboardPage: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    coursesStore() {
      return useCoursesStore();
    },
    currentCourses() {
      return this.sectionCourses('My Current Courses');
    },
    upcomingCourses() {
      return this.sectionCourses('My Upcoming Courses');
    },
    transcriptToAdd() {
      return this.sectionCourses('Add to Transcript');
    },
    transcripted() {
      return this.sectionCourses('Transcripted Courses');
    },
    pastCourses() {
      return this.sectionCourses('Past Courses');
    },
    hasTranscriptedCourses() {
      return this.transcripted.length > 0;
    },
    // Codes for the transcript-deadline banner (deadline within 60 days).
    deadlineApproachingCodes() {
      return deadlineApproachingCourseCodes(this.transcriptToAdd);
    },
    hasCourses() {
      return (
        this.currentCourses.length +
          this.upcomingCourses.length +
          this.transcriptToAdd.length +
          this.transcripted.length +
          this.pastCourses.length >
        0
      );
    },
    courseTabs() {
      return [
        { value: 'all', label: 'All' },
        { value: 'current', label: 'Current' },
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'past', label: 'Past', badge: this.transcriptToAdd.length }
      ];
    }
  },

  watch: {
    // A view-as enter or exit flips this; the page's own first load is init's.
    'refreshDashboardPage.dashboard'() {
      this.getEnrollments((enrollments) => {
        this.logInfo('get enrollments', enrollments);
      }, 'courseHistory');
    }
  },

  methods: {
    sectionCourses(title) {
      const section = (this.coursesStore.globalCourseSections || []).find((s) => s.section_title === title);
      return section?.courses || [];
    },

    // Delegates to the shared mapping so the page and its stories can't drift.
    groupsFor(tab) {
      return courseGroupsFor(tab, {
        current: this.currentCourses,
        upcoming: this.upcomingCourses,
        transcriptToAdd: this.transcriptToAdd,
        transcripted: this.transcripted,
        past: this.pastCourses
      });
    },

    emptyState(tab) {
      return emptyStateFor(tab);
    },

    sectionCopy(section) {
      return sectionCopyFor(section);
    },

    subheaderText(tab) {
      return subheaderTextFor(tab, {
        current: this.currentCourses,
        upcoming: this.upcomingCourses,
        transcriptToAdd: this.transcriptToAdd,
        transcripted: this.transcripted,
        past: this.pastCourses
      });
    },

    courseProps(course, sectionTitle) {
      return {
        title: course._courseInfo._courseName,
        coursePrefix: course._courseInfo._coursePrefix,
        coursePrefixDisplayLabel: course._courseInfo._coursePrefixDisplayLabel || '',
        term: course._courseInfo._term,
        courseStartDate: course._courseInfo._courseStartDate,
        creditConvertedDate: course._creditConvertedDate,
        courseEndDate: course._courseInfo._courseEndDate,
        enrollmentDate: course._enrollmentDate,
        credits: course._courseInfo._asuCredits,
        shouldShowViewCourse: !['Transcripted Courses', 'Past Courses'].includes(sectionTitle),
        courseCompletedDate: course._courseCompletedDate,
        progressPageURL: course._progressPageURL,
        enrollmentStatus: course._enrollmentStatus,
        courseUrl: course._courseInfo._openEdxCourseUrl,
        buttonLabel: course._buttonLabel,
        buttonAction: course._buttonAction,
        courseType: course._courseInfo._courseType,
        grade: course._grade || 0,
        currentGrade: course._currentGrade || '0',
        finalGrade: course._finalGrade || '',
        from: 'course-history',
        courseSection: sectionTitle,
        courseViewUrl: course._courseInfo._openEdxViewCourseUrl,
        courseOfferingId: course._courseOfferingId,
        creditConversionPrice: course._courseInfo._creditConversionPrice,
        enrollmentId: course._enrollmentId,
        creditEligibilityPrice: course._courseInfo._creditEligibilityPrice,
        courseInfo: course._courseInfo,
        transcriptedDate: course._transcriptedDate,
        creditWindowOverride: course._creditWindowOverride,
        isCourseResetAllowed: course._courseInfo._isCourseResetAllowed,
        // Kept in step with DashboardCoursesPanel.getCourseProps — the card's CTA
        // and Options menu read these, so omitting any of them silently drops UI.
        unenroll: course._unenroll,
        courseSelected: (course._enrollmentStatus || '').toLowerCase() === 'active',
        isPathwayCourse: course._isPathwayCourse,
        paymentMethod: course._paymentMethod,
        enrollmentModeVerifiedDate: course._enrollmentModeVerifiedDate,
        courseDropDeadline: course._courseDropDeadline,
        courseRegistrationDate: course._courseInfo._courseRegistrationDate,
        orientationStartDate: course._courseInfo._orientationStartDate,
        isCourseRefunded: course._isCourseRefunded,
        verifiedTransactionId: course._verifiedTransactionId,
        multiEnrollmentType: course._multiEnrollmentType,
        courseLength: course._courseInfo._courseLength,
        coursePrerequisiteLabel: course._courseInfo._coursePrerequisiteLabel,
        courseEffortPerHour: course._courseInfo._courseEffortPerHour,
        coursePacing: course._courseInfo._coursePacing,
        canvasCourseId: course._canvasCourseId,
        canvasEnrollmentId: course._courseInfo._canvasEnrollmentId,
        canvasUserId: course._canvasUserId || null,
        benefitType: course._benefitType,
        benefitsEligible: course._benefitsEligible,
        partnerBenefitEligible: course._partnerBenefitEligible,
        idVerificationDeadline: course._idVerificationDeadline,
        deadlineCounter: course._courseInfo._deadlineCounter,
        courseBrief: course._courseInfo._courseBrief,
        eaCourseURL: course._courseInfo._eaCourseURL,
        enrollmentMode: course._enrollmentMode,
        courseDescription: course._courseInfo._courseDescription,
        openEdxCourseId: course._courseInfo._openEdxCourseId,
        LMS: course._courseInfo._lms,
        invisibleVoucher: course._invisibleVoucher,
        completionLtiUrl: course._completionLtiUrl,
        courseDesignations: course._courseInfo._courseDesignations
      };
    },

    openCloseUnenrollModel(event) {
      if (event) this.courseUnenrollInfo = event.courseUnenrollInfo;
      this.unenrollCourse = !this.unenrollCourse;
    },

    reloadCourses() {
      this.getEnrollments((enrollments) => {
        this.logInfo('get enrollments', enrollments);
      }, 'courseHistory');
    },

    // Mirrors DashboardCoursesPanel.doUnenroll; the store is shared so the list
    // re-renders off the refreshed enrollments.
    async doUnenroll(event) {
      try {
        const enrollment = this.coursesStore.enrollments.find((e) => e._enrollmentId === event.enrollmentId);

        if (!enrollment) throw new Error('Enrollment not found for id ' + event.enrollmentId);

        await this.$conn.unenrollCourse({
          learnerId: useUserStore().apis.learnerData._learnerId,
          enrollmentId: enrollment._enrollmentId,
          courseOfferingId: enrollment._courseOfferingId,
          enrollmentStatus: 'Inactive'
        });

        this.getEnrollments((enrollments) => this.logInfo('get enrollments', enrollments));

        if (typeof event.callback === 'function') event.callback();
      } catch (error) {
        this.logError('dounenroll failed', error);
      }
    },

    handleAddCoursesClick() {
      this.asuoDataLayerClick('AddCourses', '2', { component: 'Add Courses' });
      this.$router.push('/courses-add');
    },

    shouldRemoveCreditTracker() {
      const subDomain = this.$conn.learnerData?._partnerURLSubDomain;
      return subDomain === 'hs' || subDomain === 'hs4cc' || !this.$conn.partnerData.isEarnedAdmission;
    }
  },

  created() {
    this.init('courseHistory', () => {
      this.earnedCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsCompleted;
      this.totalCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsNeeded;
      this.currentGPA = parseFloat(this.$conn.learnerData.dashboardPageData.currentGPA).toFixed(1);

      this.ajaxLoaded = true;
    });
  }
});
</script>
